import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiSearch, FiBell, FiChevronDown, FiChevronUp, FiFilter, FiCalendar } from "react-icons/fi";
import { noticeAPI } from "../services/api";
import { formatDate } from "../utils/helpers";
import { useDataRefresh } from "../hooks/useDataRefresh";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useSiteSettings } from "../context/SiteSettingsContext";

const CATEGORIES = ["All", "General", "Academic", "Sports", "Events", "Urgent", "Holiday", "Exam"];

const categoryColors = {
  Academic: "bg-[#2563EB] text-white",
  Holiday:  "bg-sky-400 text-white",
  Exam:     "bg-[#1e3a8a] text-white",
  Events:   "bg-[#0EA5E9] text-white",
  General:  "bg-slate-500 text-white",
  Urgent:   "bg-red-500 text-white",
  Sports:   "bg-[#1e40af] text-white",
};

const Notices = () => {
  const { settings } = useSiteSettings();
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [expanded, setExpanded] = useState(null);

  const { data: notices = [], loading } = useDataRefresh(
    async () => {
      const response = await noticeAPI.getAll();
      return response?.data?.data?.notices || [];
    },
    "notices",
    { pollInterval: 10000 },
  );

  useEffect(() => {
    let result = notices;
    if (category !== "All") result = result.filter((n) => n.category === category);
    if (search.trim()) result = result.filter((n) => n.title.toLowerCase().includes(search.toLowerCase()));
    setFiltered(result);
    setExpanded(null);
  }, [category, search, notices]);

  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="bg-[#1e3a8a] py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Stay Informed</p>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-6">
              Important <span className="text-sky-300">Notices</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-xl leading-relaxed">
              Important announcements and notices from {settings?.schoolName || "Hillbert English Boarding School "}.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 sm:px-12 lg:px-16 py-16 lg:py-24">
        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-10">
          <div className="relative flex-1">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search notices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-[#F0F4FF] border border-blue-100 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB] text-slate-900 transition-all"
            />
          </div>
          <div className="flex items-center gap-0 rounded-xl overflow-hidden border border-blue-100 bg-[#F0F4FF]">
            <div className="w-12 h-full bg-[#2563EB] flex items-center justify-center shrink-0 px-3">
              <FiFilter className="text-white w-4 h-4" />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="pl-4 pr-10 py-4 bg-transparent border-0 text-sm font-bold uppercase tracking-widest focus:ring-0 text-slate-700 transition-colors appearance-none"
            >
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-3">
            {filtered.map((notice, idx) => (
              <motion.div
                key={notice._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.06 }}
                className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setExpanded(expanded === notice._id ? null : notice._id)}
                  className="w-full flex items-start sm:items-center gap-5 p-5 sm:p-6 text-left hover:bg-[#F0F4FF] transition-colors group"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                    notice.category === "Urgent"
                      ? "bg-red-500 group-hover:bg-red-600"
                      : "bg-[#2563EB] group-hover:bg-[#1D4ED8]"
                  }`}>
                    <FiBell className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-1.5">
                      <h3 className={`font-bold text-base sm:text-lg transition-colors ${expanded === notice._id ? "text-[#2563EB]" : "text-[#0F172A]"}`}>
                        {notice.title}
                      </h3>
                      <span className={`px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-lg ${categoryColors[notice.category] || categoryColors.General}`}>
                        {notice.category}
                      </span>
                    </div>
                    {notice.publishDate && (
                      <div className="flex items-center gap-2 text-slate-400 text-xs">
                        <FiCalendar className="w-3 h-3 text-[#2563EB]" />
                        {formatDate(notice.publishDate)}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 mt-1 sm:mt-0">
                    {expanded === notice._id ? (
                      <FiChevronUp className="w-5 h-5 text-[#2563EB]" />
                    ) : (
                      <FiChevronDown className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
                    )}
                  </div>
                </button>

                <AnimatePresence>
                  {expanded === notice._id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-0">
                        <p className="text-slate-600 text-sm leading-relaxed border-t border-blue-50 pt-5">
                          {notice.content}
                        </p>
                        {notice.expiryDate && (
                          <p className="mt-4 text-[11px] text-slate-400 bg-[#F0F4FF] inline-block px-3 py-1.5 rounded-lg border border-blue-100">
                            Notice expires: {formatDate(notice.expiryDate)}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}

            {filtered.length === 0 && (
              <div className="text-center py-24 bg-[#F0F4FF] rounded-3xl border border-blue-100">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <FiBell className="w-8 h-8 text-[#2563EB]" />
                </div>
                <p className="text-slate-500 font-semibold text-lg">No notices found</p>
                <p className="text-slate-400 text-sm mt-2">Try adjusting your search or filter.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notices;
